import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import {WorkstreamCreate as WorkstreamCreatePayload, WorkstreamPrivacy} from 'client';
import {PageSpinner} from 'components/shared/PageSpinner';
import {useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {workstreamsPath} from 'views/workstreams/page/workstreamsRoutes';

import {useWorkstreamCreate} from './hooks';
import {WorkstreamModify} from './WorkstreamModify';

const DEFAULT_NEW_WORKSTREAM_NAME = 'Workstream name…';

export const buildDefaultWorkstreamPayload = (
  userId: string,
  type: 'workstream' | 'tracker'
): WorkstreamCreatePayload => ({
  name: DEFAULT_NEW_WORKSTREAM_NAME,
  description: '',
  start_date: new Date().toISOString(),
  end_date: null,
  status_cadence: type === 'tracker' ? 'none' : 'semimonthly',
  status_due: 1,
  links: [],
  dependencies: [],
  employee_associations: [{user_id: userId, dri: 'dri'}],
  privacy: WorkstreamPrivacy.PUBLIC,
  private_employees: [],
  private_teams: [],
});

type WorkstreamCreateLocationState = {
  source?: string;
  scope_type?: string;
  scope_value?: string;
  startTitleEditing?: boolean;
  type?: 'workstream' | 'tracker';
  workstreamId?: number;
};

export const WorkstreamCreate = () => {
  const {createWorkstream, isPending} = useWorkstreamCreate();
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const autoCreateStartedRef = useRef(false);
  const [hasAutoCreateFailed, setHasAutoCreateFailed] = useState(false);
  const locationState =
    location.state && typeof location.state === 'object'
      ? (location.state as WorkstreamCreateLocationState)
      : undefined;
  const createType = locationState?.type === 'tracker' ? 'tracker' : 'workstream';
  const shouldAutoCreateViaDetails = createType === 'workstream' && !hasAutoCreateFailed;

  useEffect(() => {
    if (!shouldAutoCreateViaDetails || autoCreateStartedRef.current || !employee.user_id) {
      return;
    }

    autoCreateStartedRef.current = true;

    void createWorkstream({
      data: buildDefaultWorkstreamPayload(employee.user_id, createType),
    })
      .then((workstreamId) => {
        // The full page, where the hero picks up startTitleEditing (the
        // focused card at /workstreams/:id can't rename).
        navigate(workstreamsPath(workstreamId, 'detail'), {
          replace: true,
          state: {
            ...(locationState ?? {}),
            source: 'create',
            workstreamId,
            startTitleEditing: true,
          },
        });
      })
      .catch((error: unknown) => {
        setHasAutoCreateFailed(true);
        setSnackbarMessage({
          text: error instanceof Error && error.message ? error.message : 'Something went wrong',
        });
      });
  }, [
    createType,
    createWorkstream,
    employee.user_id,
    locationState,
    navigate,
    setSnackbarMessage,
    shouldAutoCreateViaDetails,
  ]);

  if (shouldAutoCreateViaDetails) {
    return <PageSpinner />;
  }

  return (
    <WorkstreamModify
      workstream={undefined}
      isPending={isPending}
      onSubmit={({data}) => createWorkstream({data})}
      disableModifyOutcomes
    />
  );
};
